import { useOverlayPortalRoot } from "@/components/modal/overlay.hooks";
import { CHALK_ADMIN_SURFACE_CLASS } from "@/components/admin/admin-visual-treatment";
import { useFlagsV2 } from "@/utils/hooks/use-flags-grpc";
import { shouldSidebarCollapseOnEnter } from "@/components/context-providers/GlobalAppStateWrapper";
import { ViewportAwareFixedMenuV2 } from "@/components/interactive/ViewportAwareFixedMenuV2";
import { MenuItemV2 } from "@/components/interactive/MenuItemV2";
import { MenuContainerV2, MenuSectionV2 } from "@/components/interactive/MenuContainerV2";
import { TooltipV2 } from "@/components/TooltipV2";
import { IconButtonV2 } from "@/components/interactive/ButtonV2";
import { InputV2 } from "@/components/interactive/InputV2";
import { ViewportAwareSidebarToggleShortcutTooltip } from "@/components/interactive/SidebarToggleShortcutTooltip";
import { ViewportAwareShortcutTooltipV2 } from "@/components/interactive/ShortcutTooltipV2";
import { openCommandPalette } from "@/components/command-palette/open-command-palette";
import { useRedSurfacesSuppressed } from "@/components/admin/use-admin-surface";
import { Icon } from "@/components/icons/Icon";
import {
  SideNavV2,
  SideNavV2Item,
  SideNavV2Section,
  SideNavWorkspaceAvatarV2,
} from "@/components/interactive/SideNavV2";
import { isEditableKeyboardTarget } from "@/components/layout/details-sidebar-shortcut";
import { CollapsedNavbarAdminIndicators } from "@/components/navigation/CollapsedNavbarImpersonationIndicator";
import { ProjectNavbarProps } from "@/components/navigation/ProjectNavbar.types";
import { ProjectNavbarAdminModeControl } from "@/components/navigation/ProjectNavbarAdminModeControl";
import {
  findProjectNavCategory,
  findProjectNavCategoryListingRow,
  getProjectNavCategoryIcon,
  getProjectNavCategoryLandingItem,
  getProjectNavCategorySidebarSections,
  getProjectNavRouteCategories,
  isProjectSecondarySidebarPinned,
  withStoredSidebarPreference,
  findProjectNavCategoryForPath,
  projectNavCategoryContains,
} from "@/components/navigation/project-category-nav";
import { getProjectNavigationRouteKey } from "@/components/navigation/project-navigation-route-key";
import { RAIL_ICON_SIZE_CLASS } from "@/components/navigation/rail-icon-size";
import {
  EntityPreviewHoverCard,
  type EntityPreviewHoverCardProps,
} from "@/components/entity-preview/EntityPreview";
import { type ExploreRecent, useExploreRecents } from "@/components/navigation/use-explore-recents";
import { PROJECT_NAV_RECENT_ENABLED } from "@/components/navigation/project-nav-history";
import { prettyFormatAgo } from "@/utils/date-time";
import { useProjectNavPins } from "@/components/navigation/project-nav-pins";
import {
  applyStoredOrder,
  collectPinnableRailItems,
  resolveRailPins,
  useProjectRailPins,
  useRailCategoryOrder,
} from "@/components/navigation/project-nav-rail-pins";
import {
  applySidebarCategoryLabels,
  applySidebarRowLayout,
  categoryTradesRows,
  getAddedSidebarSections,
  NEW_SIDEBAR_SECTION_LABEL,
  getArrangedCategoryLandingItem,
  getCategorySidebarSectionsWithMoves,
  resolveRowCategory,
  storedSectionOrder,
  useSidebarRowLayout,
  useSidebarSectionOrder,
  useSidebarSectionOrders,
} from "@/components/navigation/project-nav-sidebar-layout";
import { useCategoryRailRowDrop } from "@/components/navigation/category-rail-row-drop";
import {
  aliasProjectNavItemId,
  resolveSideNavPointerActiveItemId,
} from "@/components/navigation/project-nav-active-item";
import {
  ProjectSideNavModel,
  useProjectSideNavModel,
} from "@/components/navigation/use-project-side-nav-model";
import {
  GLOBAL_NAV_CATEGORY_ID,
  buildGlobalNavCategory,
} from "@/components/navigation/global-nav-category";
import { useGlobalNavReturnHref } from "@/components/navigation/nav-link-environment";
import {
  CATEGORY_RAIL_COLLAPSE_TRANSITION_CLASS,
  useCategoryRailCollapse,
} from "@/components/navigation/category-rail-collapse";
import { setNavigationV2AppReturnPath } from "@/components/navigation/navigation-v2-return-path";
import { useTeamSideNavItems } from "@/components/navigation/use-team-side-nav-items";
import { useTransitionAcrossPages } from "@/components/navigation/transition-across-pages";
import { ZIndex, ZIndexClass } from "@/constants/z-index";
import { useGlobalAppState } from "@/utils/app-state";
import { classNames } from "@/utils/classnames";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { useCanArrangeCategoryNav } from "@/utils/hooks/use-category-nav-enabled";
import { useDragReorder } from "@/utils/hooks/use-drag-reorder";
import { useLocalStorageSyncedState } from "@/utils/hooks/use-local-storage-synced-state";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import { MOTION_DURATION_CLASS, MOTION_DURATION_MS } from "@/utils/motion";
import { isSidebarToggleShortcut } from "@/utils/sidebar-toggle-shortcut";
import { safePolygon, useFloating } from "@floating-ui/react";
import { useTheme } from "next-themes";
import { useRouter } from "next/router";
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export const PROJECT_SECONDARY_SIDEBAR_WIDTH_PX = 240;
const RAIL_WIDTH_PX = 64;
// Where the pinned sidebar's content begins when the slot cannot be measured: right of the
// rail and below the 48px top rail (TOP_RAIL_HEIGHT), inside the elevated pane's 1px border.
const ELEVATED_PANE_BORDER_PX = 1;
const FALLBACK_SIDEBAR_CONTENT_TOP_PX = 48 + ELEVATED_PANE_BORDER_PX;
// The hover panel floats this far inside the pinned sidebar's column on every side.
const HOVER_PANEL_INSET_PX = 6;
// Leaving the panel, or the path from its icon to it, closes the panel after this grace period,
// which is long enough to reach the next icon without the panel blinking out.
const HOVER_PANEL_CLOSE_DELAY_MS = 120;
// A rail button's hit area reaches this far right of it, as far as it reaches above and below a
// collapsed icon (`before:-right-cds-4` in `railButtonClassName`).
const RAIL_BUTTON_HIT_RIGHT_PX = 4;
// The channel between a rail button's hit area and its hover panel keeps the panel open while the
// pointer crosses it, but a pointer resting there this long closes it: a short way to dismiss the
// panel without leaving past its far edge.
const HOVER_PANEL_CHANNEL_REST_MS = MOTION_DURATION_MS.long;
// How long a row click may take to start a route change before the panel stops waiting for one.
const HOVER_PANEL_NAVIGATION_START_GRACE_MS = 250;
const SECONDARY_SIDEBAR_SLOT_ATTRIBUTE = "data-project-secondary-sidebar";

interface HoverPanelGeometry {
  /** Viewport position of the pinned sidebar's content box, which the panel's content matches. */
  contentLeft: number;
  contentTop: number;
  /** The content box's height, which ends above the assistant's rail when it shows. */
  contentHeight: number;
}

/** Where the pinned sidebar's content sits, which the hover panel floats inside. */
const measureHoverPanelGeometry = (rail: HTMLElement | null): HoverPanelGeometry => {
  // The slot is mounted even while unpinned (at zero width), so it marks where pinned content
  // sits, including when banners push the elevated pane down or the assistant's rail lifts it.
  const slotRect = document
    .querySelector(`[${SECONDARY_SIDEBAR_SLOT_ATTRIBUTE}]`)
    ?.getBoundingClientRect();
  return slotRect != null
    ? { contentLeft: slotRect.left, contentTop: slotRect.top, contentHeight: slotRect.height }
    : {
        contentLeft: (rail?.getBoundingClientRect().right ?? 0) + ELEVATED_PANE_BORDER_PX,
        contentTop: FALLBACK_SIDEBAR_CONTENT_TOP_PX,
        contentHeight: window.innerHeight - FALLBACK_SIDEBAR_CONTENT_TOP_PX,
      };
};

type ThemeMode = "light" | "dark" | undefined;

const useSideNavThemeMode = (): ThemeMode => {
  const { resolvedTheme } = useTheme();
  return resolvedTheme === "dark" ? "dark" : resolvedTheme === "light" ? "light" : undefined;
};

const NO_STORED_SIDEBAR_VISIBILITY: { hidden?: unknown } = {};

/** Whether the secondary sidebar is pinned open beside the page; see `isProjectSecondarySidebarPinned`. */
const useProjectSecondarySidebarPinned = (defaultCollapsed: boolean, settingsActive: boolean) => {
  const router = useRouter();
  const { state, updateState } = useGlobalAppState();
  // The global sidebar state is in memory only, as the classic nav wants; the category nav keeps
  // its reader's last show or hide across reloads here.
  const [storedVisibility, setStoredVisibility] = useLocalStorageSyncedState<{ hidden?: unknown }>(
    LocalStorageKey.PROJECT_SECONDARY_SIDEBAR_VISIBILITY,
    NO_STORED_SIDEBAR_VISIBILITY,
  );
  const storedPinned =
    typeof storedVisibility.hidden === "boolean" ? !storedVisibility.hidden : undefined;
  const routeKey = getProjectNavigationRouteKey(router.asPath);
  const routeEntryCollapses = shouldSidebarCollapseOnEnter(router.asPath);
  const effectiveState = withStoredSidebarPreference(state, storedPinned, routeEntryCollapses);
  const pinned = isProjectSecondarySidebarPinned({
    defaultCollapsed,
    routeEntryCollapses,
    settingsActive,
    routeKey,
    state: effectiveState,
  });
  // Brings the in-memory state, which a page load resets to open, in line with the stored choice.
  const memoryStale = effectiveState !== state;
  useEffect(() => {
    if (!memoryStale) return;
    updateState({
      sidebarOpen: effectiveState.sidebarOpen,
      sidebarOpenPreference: effectiveState.sidebarOpenPreference,
    });
  }, [effectiveState.sidebarOpen, effectiveState.sidebarOpenPreference, memoryStale, updateState]);
  const setPinned = useCallback(
    (nextPinned: boolean) => {
      if (defaultCollapsed) {
        updateState({ projectSecondarySidebarPinnedRouteKey: nextPinned ? routeKey : undefined });
        return;
      }

      updateState({ sidebarOpen: nextPinned, sidebarOpenPreference: nextPinned });
      setStoredVisibility({ hidden: !nextPinned });
    },
    [defaultCollapsed, routeKey, setStoredVisibility, updateState],
  );

  return [pinned, setPinned] as const;
};

interface ProjectCategoryNavModel extends ProjectSideNavModel {
  categories: readonly SideNavV2Section<string>[];
  /** The global settings pages, behind the rail's workspace mark. */
  globalCategory: SideNavV2Section<string>;
  activeCategory: SideNavV2Section<string> | undefined;
  /** Each category's own name, before the reader's renames. */
  defaultCategoryLabels: ReadonlyMap<string, string | undefined>;
  /** The secondary-sidebar row the route is on; Settings routes report their pane id. */
  activeItemId: string | undefined;
  /** The route is a global settings page, which spans every project and environment. */
  onGlobalSettingsPage: boolean;
  /** The global settings sidebar's row the route is on. */
  globalActiveItemId: string | undefined;
}

/** Where a category's rail icon lands: the first page its sidebar lists, as the reader arranged it. */
const getCategoryLandingItem = (
  category: SideNavV2Section<string>,
  categories: readonly SideNavV2Section<string>[],
  rowLayout: Parameters<typeof getArrangedCategoryLandingItem>[2],
  sectionOrders: Parameters<typeof storedSectionOrder>[0],
) =>
  (category.id === GLOBAL_NAV_CATEGORY_ID
    ? undefined
    : getArrangedCategoryLandingItem(
        category,
        categories,
        rowLayout,
        storedSectionOrder(sectionOrders, category.id),
      )) ?? getProjectNavCategoryLandingItem(category);

const SETTINGS_CATEGORY_ID = "configuration";

/**
 * Where the Settings root opens in the category nav: the page Settings' rail icon lands on. Not
 * `ready` until the flags that decide which rows show have loaded.
 */
export const useSettingsLandingHref = (): {
  ready: boolean;
  href: SideNavV2Item<string>["href"];
} => {
  const model = useProjectCategoryNavModel({ activePage: "settings" });
  const { rowLayout } = useSidebarRowLayout();
  const { sectionOrders } = useSidebarSectionOrders();
  const flagsReady = useFlagsV2().ready;
  const settings = model.categories.find((category) => category.id === SETTINGS_CATEGORY_ID);
  return {
    ready: flagsReady,
    href:
      settings == null
        ? undefined
        : getCategoryLandingItem(settings, model.categories, rowLayout, sectionOrders)?.href,
  };
};

const useProjectCategoryNavModel = (props: ProjectNavbarProps): ProjectCategoryNavModel => {
  const model = useProjectSideNavModel(props);
  const { inlineItems, adminItems } = useTeamSideNavItems("ProjectCategoryNavV2");
  const globalCategory = useMemo(
    () => buildGlobalNavCategory({ inlineItems, adminItems }),
    [adminItems, inlineItems],
  );
  const onGlobalSettingsPage = props.globalSettings != null;
  const globalActiveItemId = props.globalSettings?.activePage;
  const { rowLayout } = useSidebarRowLayout();
  // Under the names the reader gave them, which the rail and sidebar titles show.
  const categories = useMemo(
    () =>
      applySidebarCategoryLabels(
        model.sections.filter((section) => section.hidden !== true),
        rowLayout,
      ),
    [model.sections, rowLayout],
  );
  const defaultCategoryLabels = useMemo(
    () => new Map(model.sections.map((section) => [section.id, section.label])),
    [model.sections],
  );
  // Parents render as group titles here, so a page that reports only a parent (the Deployments
  // list reports "deployments") lands on that parent's first page, as the full sidebar does.
  const routeItemId = aliasProjectNavItemId(model.rawActiveItemId);
  const routeCategories = useMemo(
    () => getProjectNavRouteCategories(categories, model.categorySettingsActive, routeItemId),
    [categories, model.categorySettingsActive, routeItemId],
  );
  // A Settings pane another category lists (Data's Vector store, More's Buckets) can move with its
  // row like any other page.
  const routeOnSettingsRow = routeCategories !== categories;
  const activeItemId = useMemo(
    () =>
      resolveSideNavPointerActiveItemId(
        routeCategories.flatMap((category) => category.items),
        routeItemId,
      ) ?? routeItemId,
    [routeCategories, routeItemId],
  );
  const projectActiveCategory = useMemo(
    () =>
      // A row the reader moved to another category takes its page there.
      (!routeOnSettingsRow &&
      activeItemId != null &&
      rowLayout.categoryByRow?.[activeItemId] != null
        ? resolveRowCategory(categories, rowLayout, activeItemId)
        : undefined) ??
      findProjectNavCategory(routeCategories, activeItemId) ??
      // A page kept off the sidebar opens its parent's category, with no row highlighted.
      findProjectNavCategory(
        categories,
        routeItemId == null ? undefined : model.hiddenItemParentIds.get(routeItemId),
      ) ??
      (model.categorySettingsActive
        ? categories.find((category) => category.items.some((item) => item.id === "settings"))
        : undefined),
    [
      activeItemId,
      categories,
      model.categorySettingsActive,
      model.hiddenItemParentIds,
      routeCategories,
      routeItemId,
      routeOnSettingsRow,
      rowLayout,
    ],
  );

  return {
    ...model,
    categories,
    globalCategory,
    activeCategory: onGlobalSettingsPage ? globalCategory : projectActiveCategory,
    activeItemId,
    onGlobalSettingsPage,
    globalActiveItemId,
    defaultCategoryLabels,
  };
};

const formatRecentActivity = (recent: ExploreRecent): string | undefined =>
  recent.activityAtMs === 0
    ? undefined
    : `${recent.activity === "edited" ? "Edited" : "Visited"} ${prettyFormatAgo(
        new Date(recent.activityAtMs),
      )}`;

/** A sidebar row's entity preview: to the row's right, clear of the sidebar's edge. */
const SidebarEntityPreview: React.FC<
  Pick<EntityPreviewHoverCardProps, "entity" | "icon" | "meta" | "children">
> = (props) => (
  <EntityPreviewHoverCard placement={"right-start"} offset={16} openDelayMs={400} {...props} />
);

const EXPLORE_PINNED_SECTION_ID = "explore:pinned";
const EXPLORE_RECENT_SECTION_ID = "explore:recent";

/** Section ids readers have collapsed, shared by every sidebar instance and kept across reloads. */
const useCollapsedSidebarSectionIds = () => {
  const { state, updateState } = useGlobalAppState();
  const stored = state.projectSidebarCollapsedSectionIds;
  const collapsedSectionIds = useMemo(
    () =>
      new Set(
        Array.isArray(stored) ? stored.filter((id): id is string => typeof id === "string") : [],
      ),
    [stored],
  );
  const setSectionCollapsed = useCallback(
    (sectionId: string, collapsed: boolean) => {
      const next = new Set(collapsedSectionIds);
      if (collapsed) next.add(sectionId);
      else next.delete(sectionId);
      updateState({ projectSidebarCollapsedSectionIds: [...next] });
    },
    [collapsedSectionIds, updateState],
  );

  return [collapsedSectionIds, setSectionCollapsed] as const;
};

/**
 * A category sidebar's title, the category's name. With `onRename`, an edit button shows beside it
 * on hover and turns it into an InputV2 at the title's size: Enter or leaving it keeps the new
 * name, Escape the old one.
 */
const SidebarCategoryTitle: React.FC<{ label: string; onRename?: (label: string) => void }> = ({
  label,
  onRename,
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(label);
  const doneRef = useRef(false);
  const finish = (commit: boolean) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setEditing(false);
    if (commit) onRename?.(value);
  };

  if (editing) {
    return (
      <InputV2
        size={"sm"}
        label={"Category name"}
        hideLabel
        value={value}
        onChange={(event) => setValue(event.currentTarget.value)}
        maxLength={32}
        // The title's height and type; 2px out so the name stays where the title had it.
        containerClassName={"!h-7 -ml-cds-2 [&_input]:!text-cds-xl [&_input]:!font-medium"}
        // Opened from its own edit button, so taking focus is what the reader asked for.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        onFocus={(event) => event.currentTarget.select()}
        onKeyDown={(event) => {
          if (event.key === "Enter") finish(true);
          if (event.key === "Escape") finish(false);
        }}
        onBlur={() => finish(true)}
      />
    );
  }

  return (
    <div className={"group/category-title flex min-w-0 items-center gap-cds-4"}>
      <h2
        className={
          "cursor-default truncate pl-cds-6 text-cds-xl font-medium text-cds-ink-strong " +
          "dark:text-zinc-50"
        }
      >
        {label}
      </h2>
      {onRename != null && (
        <IconButtonV2
          size={"xs"}
          variant={"tertiary"}
          icon={"edit"}
          aria-label={`Rename ${label}`}
          className={
            "shrink-0 opacity-0 transition-opacity duration-75 focus-visible:opacity-100 " +
            "group-hover/category-title:opacity-100"
          }
          onClick={() => {
            setValue(label);
            doneRef.current = false;
            setEditing(true);
          }}
        />
      )}
    </div>
  );
};

const SEARCH_SHORTCUT_TOOLTIP_KEYS = [{ value: "⌘", ariaLabel: "Command" }, { value: "K" }];

/**
 * The search button at the sidebar title row's right end, level with the title: opens the command
 * palette, as the top rail's search field and ⌘K do, with the shortcut in its tooltip.
 */
const SidebarSearchButton: React.FC = () => {
  const overlayPortalRoot = useOverlayPortalRoot();
  const themeMode = useSideNavThemeMode();
  const themeClass =
    themeMode === "dark" ? "dark" : themeMode === "light" ? "theme-light" : undefined;
  const tooltipId = useId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(
    null,
  );
  const showTooltip = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect == null) return;
    setTooltipPosition({
      left: rect.left + rect.width / 2,
      top: rect.bottom + SIDEBAR_TOGGLE_TOOLTIP_GUTTER_PX,
    });
  };
  const hideTooltip = () => setTooltipPosition(null);

  return (
    <>
      <IconButtonV2
        ref={buttonRef}
        size={"sm"}
        // The shared 14px, a step over the 28px button's own 12px, so the glyph reads at the
        // title's size.
        iconSize={"md"}
        variant={"tertiary"}
        icon={"search"}
        aria-label={"Search"}
        aria-keyshortcuts={"Meta+K"}
        aria-describedby={tooltipPosition != null ? tooltipId : undefined}
        className={"shrink-0"}
        onBlur={hideTooltip}
        onFocus={showTooltip}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onClick={(event) => {
          hideTooltip();
          // A pointer click lets go of focus, so the palette's closing does not ring it.
          if (event.detail > 0) event.currentTarget.blur();
          openCommandPalette();
        }}
      />
      {tooltipPosition != null &&
        typeof document !== "undefined" &&
        createPortal(
          <ViewportAwareShortcutTooltipV2
            id={tooltipId}
            themeClass={themeClass}
            left={tooltipPosition.left}
            top={tooltipPosition.top}
            label={"Search"}
            keys={SEARCH_SHORTCUT_TOOLTIP_KEYS}
          />,
          overlayPortalRoot ?? document.body,
        )}
    </>
  );
};

/**
 * The hover panel's show toggle, where the pinned sidebar has its hide toggle: pins the panel's
 * sidebar open. Its tooltip shows the `[` shortcut only for the current category, the one that
 * shortcut pins.
 */
const HoverPanelPinButton: React.FC<{ onPin: () => void; showsShortcut: boolean }> = ({
  onPin,
  showsShortcut,
}) => {
  const overlayPortalRoot = useOverlayPortalRoot();
  const themeMode = useSideNavThemeMode();
  const themeClass =
    themeMode === "dark" ? "dark" : themeMode === "light" ? "theme-light" : undefined;
  const tooltipId = useId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(
    null,
  );
  const showTooltip = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect == null) return;
    setTooltipPosition({
      left: rect.left + rect.width / 2,
      top: rect.bottom + SIDEBAR_TOGGLE_TOOLTIP_GUTTER_PX,
    });
  };
  const hideTooltip = () => setTooltipPosition(null);

  return (
    <>
      <IconButtonV2
        ref={buttonRef}
        size={"sm"}
        iconSize={"md"}
        variant={"tertiary"}
        icon={"panelLeftDashed"}
        aria-label={"Show sidebar"}
        aria-describedby={tooltipPosition != null ? tooltipId : undefined}
        className={"shrink-0"}
        onBlur={hideTooltip}
        onFocus={showTooltip}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onClick={(event) => {
          hideTooltip();
          if (event.detail > 0) event.currentTarget.blur();
          onPin();
        }}
      />
      {tooltipPosition != null &&
        typeof document !== "undefined" &&
        createPortal(
          showsShortcut ? (
            <ViewportAwareSidebarToggleShortcutTooltip
              id={tooltipId}
              themeClass={themeClass}
              left={tooltipPosition.left}
              top={tooltipPosition.top}
              label={"Show"}
            />
          ) : (
            <ViewportAwareShortcutTooltipV2
              id={tooltipId}
              themeClass={themeClass}
              left={tooltipPosition.left}
              top={tooltipPosition.top}
              label={"Show"}
              keys={[]}
            />
          ),
          overlayPortalRoot ?? document.body,
        )}
    </>
  );
};

const ProjectCategorySidebarV2: React.FC<{
  category: SideNavV2Section<string>;
  model: ProjectCategoryNavModel;
  /**
   * Drawn in the hover panel rather than the pinned column. The panel paints the surface, so the
   * nav's own stays clear. Sections and rows still drag, holding the panel open meanwhile
   * (`onReorderDragChange`), but titles are renamed, added, and deleted from the pinned sidebar
   * only: the panel can close under a title being typed.
   */
  floating?: boolean;
  /** The pinned sidebar's own show and hide toggle, after the search button in the title row. */
  sidebarToggle?: React.ReactNode;
  onReorderDragChange?: (dragging: boolean) => void;
  onItemSelect?: (item: SideNavV2Item<string>) => void;
  onItemActionsMenuOpenChange?: (open: boolean) => void;
}> = ({
  category,
  model,
  floating = false,
  sidebarToggle,
  onReorderDragChange,
  onItemSelect,
  onItemActionsMenuOpenChange,
}) => {
  const themeMode = useSideNavThemeMode();
  const { projectId, environmentId } = useRouteAndTeamInfo();
  const router = useRouter();
  const [collapsedSectionIds, setSectionCollapsed] = useCollapsedSidebarSectionIds();
  // Pinned and Recent follow SQL and Notebooks, whose worksheets and notebooks they list.
  const showsPinnedAndRecent = category.id === "compute";
  const { pins: pagePins, pin: pinPage, unpin: unpinPage } = useProjectNavPins();
  const pinnedPageKeys = useMemo(() => new Set(pagePins.map((pin) => pin.key)), [pagePins]);
  const recents = useExploreRecents({
    enabled: showsPinnedAndRecent && PROJECT_NAV_RECENT_ENABLED,
    includeWorksheets: projectNavCategoryContains(category, "explorer-sql"),
    includeNotebooks: projectNavCategoryContains(category, "notebooks-nav"),
    excludeKeys: pinnedPageKeys,
  });
  // Only Chalk employees arrange the nav; everyone else gets it as it ships, with nothing to drag,
  // rename, add, delete, or pin.
  const canArrange = useCanArrangeCategoryNav();
  const { pinnedItemIds, pinToRail, unpinFromRail } = useProjectRailPins();
  const { sectionOrder, setVisibleSectionOrder } = useSidebarSectionOrder(category.id);
  const {
    rowLayout,
    arrangeRows,
    moveRowToCategory,
    renameSection,
    addSection,
    deleteSection,
    renameCategory,
    moveSectionToCategory,
  } = useSidebarRowLayout();
  const rowDrop = useCategoryRailRowDrop();
  const globalCategory = category.id === GLOBAL_NAV_CATEGORY_ID;
  const returnHref = useGlobalNavReturnHref();
  // Hovering the workspace mark elsewhere previews this sidebar under its title instead.
  const showsBackToApp = globalCategory && model.onGlobalSettingsPage && returnHref != null;
  const sections = useMemo((): SideNavV2Section<string>[] => {
    // Every row with a destination can be pinned to the rail as a shortcut, except the global
    // settings pages, which the workspace mark already leads to.
    // Rows the reader moved in from other categories lead; the global settings rows stay put.
    const categorySections = [
      ...(globalCategory
        ? getProjectNavCategorySidebarSections(category)
        : getCategorySidebarSectionsWithMoves(category, model.categories, rowLayout)
      ).map((section) => ({
        ...section,
        items: section.items.map((item): SideNavV2Item<string> => {
          if (!canArrange || globalCategory || item.href == null || item.disabled) return item;
          const pinned = pinnedItemIds.includes(item.id);
          return {
            ...item,
            actions: [
              pinned
                ? {
                    id: "unpin-from-rail",
                    label: "Unpin from rail",
                    icon: "pinOff",
                    onSelect: () => unpinFromRail(item.id),
                  }
                : {
                    id: "pin-to-rail",
                    label: "Pin to rail",
                    icon: "pin",
                    onSelect: () => pinToRail(item.id),
                  },
            ],
          };
        }),
      })),
      // Sections the reader added, empty until rows move in.
      ...(globalCategory ? [] : getAddedSidebarSections<string>(rowLayout, category.id)),
    ];
    // On a global settings page, a Back to app row leads the sidebar in place of its title.
    if (showsBackToApp) {
      return [
        {
          id: `${GLOBAL_NAV_CATEGORY_ID}:back`,
          // It leads the sidebar out, so rows neither join it nor leave.
          fixedRows: true,
          items: [
            {
              id: "back-to-app",
              label: "Back to app",
              icon: "arrowLeft",
              // Quieter than the pages below: it leaves the sidebar rather than opening one.
              visualTone: "subtle",
              // The rail waits for the environment's page rather than moving on the click: that
              // page's first render is long enough to stall a move already under way, which then
              // lands in a jump. Mounted where this page left the rail, it moves from there.
              href: returnHref,
            },
          ],
        },
        ...categorySections,
      ];
    }
    if (!showsPinnedAndRecent) return categorySections;

    const pinnedItems = pagePins.map(
      (pin): SideNavV2Item<string> => ({
        id: `pinned:${pin.key}`,
        label: pin.name,
        href: pin.href,
        icon: pin.icon,
        actions: [
          { id: "unpin", label: "Unpin", icon: "pinOff", onSelect: () => unpinPage(pin.key) },
        ],
        renderHoverPreview: (row) => (
          <SidebarEntityPreview
            entity={{ ...(pin.entity ?? { kind: "page", id: pin.key }), name: pin.name }}
            icon={pin.icon}
          >
            {row}
          </SidebarEntityPreview>
        ),
      }),
    );
    const recentItems = recents.map(
      (recent): SideNavV2Item<string> => ({
        id: `recent:${recent.key}`,
        label: recent.name,
        href: recent.href,
        icon: recent.icon,
        actions: [
          {
            id: "pin",
            label: "Pin",
            icon: "pin",
            onSelect: () =>
              pinPage({
                key: recent.key,
                name: recent.name,
                href: recent.href,
                icon: recent.icon,
                entity: recent.entity,
              }),
          },
        ],
        renderHoverPreview: (row) => (
          <SidebarEntityPreview
            entity={{ ...recent.entity, name: recent.name }}
            icon={recent.icon}
            meta={formatRecentActivity(recent)}
          >
            {row}
          </SidebarEntityPreview>
        ),
      }),
    );
    const collapsibleSection = (
      id: string,
      label: string,
      items: SideNavV2Item<string>[],
    ): SideNavV2Section<string> => ({
      id,
      label,
      items,
      collapsible: true,
      revealChevronOnHover: true,
      // Pins keep the order they were pinned in, and recents their recency.
      fixedRows: true,
    });

    return [
      ...categorySections,
      ...(pinnedItems.length > 0
        ? [collapsibleSection(EXPLORE_PINNED_SECTION_ID, "Pinned", pinnedItems)]
        : []),
      ...(recentItems.length > 0
        ? [collapsibleSection(EXPLORE_RECENT_SECTION_ID, "Recent", recentItems)]
        : []),
    ];
  }, [
    canArrange,
    category,
    globalCategory,
    model.categories,
    pagePins,
    pinPage,
    pinToRail,
    pinnedItemIds,
    recents,
    returnHref,
    rowLayout,
    showsBackToApp,
    showsPinnedAndRecent,
    unpinFromRail,
    unpinPage,
  ]);
  const orderedSections = useMemo(
    () => applySidebarRowLayout(applyStoredOrder(sections, sectionOrder), rowLayout),
    [rowLayout, sectionOrder, sections],
  );
  // Where each row sits by default, so a row dragged back home is no longer a move.
  const homeSectionByRow = useMemo(
    () =>
      new Map(sections.flatMap((section) => section.items.map((item) => [item.id, section.id]))),
    [sections],
  );
  const handleArrangeRows = useCallback(
    (arrangement: readonly { sectionId: string; itemIds: readonly string[] }[]) =>
      arrangeRows(arrangement, (rowId) => homeSectionByRow.get(rowId)),
    [arrangeRows, homeSectionByRow],
  );
  // The reader renames, adds, and deletes the docked sidebar's sections; the global settings keep
  // theirs.
  const defaultSectionLabels = useMemo(
    () => new Map(sections.map((section) => [section.id, section.label])),
    [sections],
  );
  const sectionEditing = useMemo(() => {
    if (!canArrange || floating || globalCategory) return {};
    return {
      onRenameSection: (sectionId: string, label: string) =>
        renameSection(sectionId, label, defaultSectionLabels.get(sectionId)),
      onCreateSection: (afterSectionId: string) => {
        const sectionId = `${category.id}:added:${Date.now().toString(36)}`;
        addSection(category.id, sectionId);
        const order = orderedSections.map((section) => section.id);
        order.splice(order.indexOf(afterSectionId) + 1, 0, sectionId);
        setVisibleSectionOrder(order);
        return sectionId;
      },
      onDeleteSection: (sectionId: string) => deleteSection(category.id, sectionId),
    };
  }, [
    addSection,
    canArrange,
    category.id,
    defaultSectionLabels,
    deleteSection,
    floating,
    globalCategory,
    orderedSections,
    renameSection,
    setVisibleSectionOrder,
  ]);
  // Rows also drop on other categories' rail icons, landing atop that category's sidebar.
  // Settings and the global settings keep their rows to themselves.
  const rowDropTargets = useMemo(() => {
    if (!canArrange || rowDrop == null || globalCategory || !categoryTradesRows(category)) {
      return undefined;
    }
    return {
      targetAt: (clientX: number, clientY: number) =>
        rowDrop.categoryAt(clientX, clientY, category.id),
      onTargetChange: rowDrop.setTargetCategoryId,
      onDragChange: (dragging: boolean) =>
        rowDrop.setDraggingFromCategoryId(dragging ? category.id : null),
      onDropOnTarget: (rowId: string, categoryId: string) => {
        const home = findProjectNavCategoryListingRow(model.categories, rowId);
        if (home != null) moveRowToCategory(rowId, categoryId, home.id);
      },
    };
  }, [canArrange, category, globalCategory, model.categories, moveRowToCategory, rowDrop]);
  // Whole sections drop on other categories' icons too, arriving with their title and rows. Pinned
  // and Recent list pages rather than rows of the nav, so they stay.
  const sectionDropTargets = useMemo(() => {
    if (rowDropTargets == null || rowDrop == null) return undefined;
    return {
      targetAt: (clientX: number, clientY: number, sectionId: string) =>
        orderedSections.find((section) => section.id === sectionId)?.fixedRows === true
          ? null
          : rowDrop.categoryAt(clientX, clientY, category.id),
      onTargetChange: rowDropTargets.onTargetChange,
      onDragChange: rowDropTargets.onDragChange,
      onDropOnTarget: (sectionId: string, categoryId: string) => {
        const section = orderedSections.find((candidate) => candidate.id === sectionId);
        if (section == null) return;
        moveSectionToCategory(
          {
            fromCategoryId: category.id,
            fromSectionId: section.id,
            label: section.label ?? NEW_SIDEBAR_SECTION_LABEL,
            rowIds: section.items.map((item) => item.id),
            toCategoryId: categoryId,
            sectionId: `${categoryId}:added:${Date.now().toString(36)}`,
          },
          (rowId) => findProjectNavCategoryListingRow(model.categories, rowId)?.id,
        );
      },
    };
  }, [
    category.id,
    model.categories,
    moveSectionToCategory,
    orderedSections,
    rowDrop,
    rowDropTargets,
  ]);

  return (
    <SideNavV2
      workspaceLabel={model.workspaceLabel}
      showHeader={false}
      headerAccessory={
        showsBackToApp ? undefined : (
          // Cancels SideNavV2's own padding, then restates LayoutV2's page padding (12px) and its
          // H1 indent (6px), so this title sits exactly where a page title sits in its pane.
          // Stacks above the scroll shadow that SideNavV2 paints over its top edge.
          <div
            className={classNames(
              "relative z-10 -mx-cds-12 -mt-cds-6 flex shrink-0 items-center justify-between",
              "gap-cds-8 px-cds-12 pt-cds-12",
              // A section subtitle brings its own top padding; bare rows get a little space instead.
              orderedSections[0]?.label == null && "pb-cds-4",
            )}
          >
            <SidebarCategoryTitle
              label={category.label ?? model.workspaceLabel}
              // The docked sidebar's project categories take renames; the global settings keep
              // their name.
              onRename={
                !canArrange || floating || globalCategory || category.label == null
                  ? undefined
                  : (label) =>
                      renameCategory(
                        category.id,
                        label,
                        model.defaultCategoryLabels.get(category.id),
                      )
              }
            />
            <div className={"flex shrink-0 items-center gap-cds-2"}>
              <SidebarSearchButton />
              {sidebarToggle}
            </div>
          </div>
        )
      }
      sections={orderedSections}
      onReorderSections={canArrange ? setVisibleSectionOrder : undefined}
      onArrangeRows={canArrange ? handleArrangeRows : undefined}
      onReorderDragChange={onReorderDragChange}
      rowDropTargets={rowDropTargets}
      sectionDropTargets={sectionDropTargets}
      {...sectionEditing}
      collapsedSectionIds={collapsedSectionIds}
      onSectionCollapsedChange={setSectionCollapsed}
      onItemActionsMenuOpenChange={onItemActionsMenuOpenChange}
      // Project and global row ids overlap ("settings"), so each category reads its own.
      activeItemId={globalCategory ? model.globalActiveItemId : model.activeItemId}
      onItemSelect={onItemSelect}
      // A flyout of a row's pages would float outside the hover panel and close it.
      hoverMenusDisabled
      showDetailPanes={false}
      currentPath={router.asPath}
      scrollPositionKey={`project-category-nav:${category.id}:${projectId ?? "unknown"}:${
        environmentId ?? "unknown"
      }`}
      themeMode={themeMode}
      surface={"elevated"}
      // Rows sit 12px in from the column edge rather than SideNavV2's 5px, which also keeps them
      // clear of the hover panel's inset edges.
      className={classNames(
        "h-full !w-full !rounded-none !px-cds-12",
        floating && "!bg-transparent",
      )}
      footerAccessory={<ProjectNavbarAdminModeControl />}
      aria-label={`${category.label ?? "Project"} navigation`}
    />
  );
};

/** The pinned second-level sidebar, on the elevated pane beside the page content. */
export const ProjectSecondarySidebarV2: React.FC<ProjectNavbarProps> = (props) => {
  const model = useProjectCategoryNavModel(props);
  const [pinned] = useProjectSecondarySidebarPinned(
    props.defaultCollapsed === true || props.forceCollapsed === true,
    model.categorySettingsActive,
  );
  const { collapsed: railCollapsed } = useCategoryRailCollapse();
  const returnHref = useGlobalNavReturnHref();
  // Moving between an environment and the global settings pages, the sidebar becomes the one the
  // next page shows as soon as the rail starts to move, so that page arrives to the same sidebar.
  const enteringGlobalSettings = railCollapsed && !model.onGlobalSettingsPage;
  const leavingGlobalSettings = !railCollapsed && model.onGlobalSettingsPage;
  const category = enteringGlobalSettings
    ? model.globalCategory
    : leavingGlobalSettings
      ? findProjectNavCategoryForPath(model.categories, returnHref) ?? model.activeCategory
      : model.activeCategory;
  // The global settings pages have no other navigation once the rail's icons make way for them,
  // so their sidebar stays open whatever the pin.
  const visible =
    (pinned || (railCollapsed && (enteringGlobalSettings || model.onGlobalSettingsPage))) &&
    category != null;
  const ref = useRef<HTMLDivElement | null>(null);
  const width = `${visible ? PROJECT_SECONDARY_SIDEBAR_WIDTH_PX : 0}px`;
  useTransitionAcrossPages("project-secondary-sidebar:width", ref, "width", width);

  return (
    <div
      ref={ref}
      className={classNames(
        "h-full shrink-0 overflow-hidden ease-out",
        `transition-[width] ${MOTION_DURATION_CLASS.medium} motion-reduce:transition-none`,
        visible &&
          "border-r-[1px] border-cds-surface-border dark:border-white/[0.08] dark:bg-cds-elevated-dark",
      )}
      style={{ width }}
      {...{ [SECONDARY_SIDEBAR_SLOT_ATTRIBUTE]: "" }}
      aria-hidden={!visible || undefined}
      inert={!visible || undefined}
    >
      {category != null && (
        <div className={"h-full"} style={{ width: PROJECT_SECONDARY_SIDEBAR_WIDTH_PX }}>
          <ProjectCategorySidebarV2
            category={category}
            model={model}
            // While pinned, the sidebar carries its own toggle, and the top rail closes up the slot
            // that holds it otherwise (`useProjectSecondarySidebarPinnedFor`). It stays through a
            // close, clipped with the rest as the column narrows. The global settings sidebar
            // cannot be hidden, so it has none.
            sidebarToggle={
              model.onGlobalSettingsPage || enteringGlobalSettings ? undefined : (
                <ProjectSecondarySidebarToggleV2 {...props} inSidebar />
              )
            }
          />
        </div>
      )}
    </div>
  );
};

const SIDEBAR_TOGGLE_TOOLTIP_GUTTER_PX = 4;
// The global app state key for a category pinned from another page's hover panel, which the
// page it opens pins on arrival. Read once and cleared.
const PIN_ON_ARRIVAL_STATE_KEY = "projectSecondarySidebarPinOnArrival";

/**
 * Pins or unpins the secondary sidebar; it leads the top rail, ahead of the breadcrumbs. It stays
 * in place but disabled on routes outside every category. On the global settings pages the top
 * rail closes up its slot, so it is disabled there only while that slot slides shut.
 */
/**
 * Whether the secondary sidebar is pinned open, for the top rail: while it is, the sidebar's title
 * row carries the toggle, and the rail closes up its own slot for it.
 */
export const useProjectSecondarySidebarPinnedFor = (props: ProjectNavbarProps): boolean => {
  const { categorySettingsActive } = useProjectSideNavModel(props);
  const [pinned] = useProjectSecondarySidebarPinned(
    props.defaultCollapsed === true || props.forceCollapsed === true,
    categorySettingsActive,
  );
  return pinned;
};

export const ProjectSecondarySidebarToggleV2: React.FC<
  ProjectNavbarProps & {
    /** In the pinned sidebar's title row: the search button's size, beside it. */
    inSidebar?: boolean;
  }
> = ({ inSidebar = false, ...props }) => {
  const overlayPortalRoot = useOverlayPortalRoot();
  const themeMode = useSideNavThemeMode();
  const themeClass =
    themeMode === "dark" ? "dark" : themeMode === "light" ? "theme-light" : undefined;
  const model = useProjectCategoryNavModel(props);
  const [pinned, setPinned] = useProjectSecondarySidebarPinned(
    props.defaultCollapsed === true || props.forceCollapsed === true,
    model.categorySettingsActive,
  );
  const tooltipId = useId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(
    null,
  );
  // A global settings page's sidebar always shows, so there the toggle is disabled, in a slot the
  // top rail keeps closed.
  const disabled = model.activeCategory == null || model.onGlobalSettingsPage;
  const shown = pinned || model.onGlobalSettingsPage;
  const showTooltip = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect == null || disabled) return;

    setTooltipPosition({
      left: rect.left + rect.width / 2,
      top: rect.bottom + SIDEBAR_TOGGLE_TOOLTIP_GUTTER_PX,
    });
  };
  const hideTooltip = () => setTooltipPosition(null);

  return (
    <>
      <IconButtonV2
        ref={buttonRef}
        size={inSidebar ? "sm" : "md"}
        // The shared 14px, a step under the rails' 16px icons.
        iconSize={"md"}
        variant={"tertiary"}
        // As in the classic sidebar: a solid panel while the sidebar is pinned open, a dashed one
        // while it is hidden and only floats out on hover.
        icon={shown ? "panelLeft" : "panelLeftDashed"}
        disabled={disabled}
        aria-label={shown ? "Hide sidebar" : "Show sidebar"}
        aria-pressed={!disabled && shown}
        aria-describedby={tooltipPosition != null ? tooltipId : undefined}
        className={"shrink-0"}
        onBlur={hideTooltip}
        onFocus={showTooltip}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onClick={(event) => {
          hideTooltip();
          // A pointer click lets go of focus: a key pressed while the button kept it, such as the
          // `[` shortcut, would turn its focus ring on. Enter and Space (detail 0) keep focus.
          if (event.detail > 0) event.currentTarget.blur();
          setPinned(!pinned);
        }}
      />
      {tooltipPosition != null &&
        typeof document !== "undefined" &&
        createPortal(
          <ViewportAwareSidebarToggleShortcutTooltip
            id={tooltipId}
            themeClass={themeClass}
            left={tooltipPosition.left}
            top={tooltipPosition.top}
            label={pinned ? "Hide" : "Show"}
            shortcut={model.rawActiveItemId === "sql-explorer" ? "action" : "bracket"}
          />,
          overlayPortalRoot ?? document.body,
        )}
    </>
  );
};

/**
 * Keeps the last non-empty `value` rendered until `shown` has been false for `exitMs`, so an
 * element can transition out before it unmounts. `shown` turns on a frame after mount, once the
 * hidden styles have painted, so the enter transition has a starting point.
 */
const useTransitionPresence = (value: string | undefined, exitMs: number) => {
  const [rendered, setRendered] = useState<string | undefined>(value);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (value != null) {
      setRendered(value);
      let innerFrame = 0;
      const outerFrame = window.requestAnimationFrame(() => {
        innerFrame = window.requestAnimationFrame(() => setShown(true));
      });
      return () => {
        window.cancelAnimationFrame(outerFrame);
        window.cancelAnimationFrame(innerFrame);
      };
    }

    setShown(false);
    const timeoutId = window.setTimeout(() => setRendered(undefined), exitMs);
    return () => window.clearTimeout(timeoutId);
  }, [exitMs, value]);

  return { rendered: value ?? rendered, shown: value != null && shown };
};

// TEMPORARY (nav concept debugging): logs why the hover panel opens and closes on localhost.
const logHoverPanel = (event: string) => {
  if (typeof window !== "undefined" && window.location.hostname === "localhost") {
    console.info(`[nav-hover] ${event}`);
  }
};

/** The rail's current-page fill. */
const railActiveFillClassName = (isDarkMode: boolean) =>
  isDarkMode ? "bg-white/[0.12]" : "bg-zinc-200";
const railActiveTextClassName = (isDarkMode: boolean) =>
  isDarkMode ? "!text-white [&_svg]:!text-white" : "text-cds-ink-strong";

/** One rail button: a category or a pinned shortcut. Both share size, hit area, and states. */
const railButtonClassName = ({
  active,
  highlighted,
  admin,
  isDarkMode,
  activeFillSlides = false,
  labelShown = false,
  inertWhenActive = false,
}: {
  active: boolean;
  /** Shows the hover state without hover, such as while the button's panel or menu is open. */
  highlighted: boolean;
  admin: boolean;
  isDarkMode: boolean;
  /** The current-page fill is the rail's sliding one beneath the button, not the button's own. */
  activeFillSlides?: boolean;
  /** Its `RailLabel` is open, so the hit area covers it too. */
  labelShown?: boolean;
  /** Clicking it does nothing while active, so it drops the pointer cursor then. */
  inertWhenActive?: boolean;
}) =>
  classNames(
    // The fills sit 4px inside each 40px slot: the margin keeps the slot, so nothing around the
    // button moves. A following `RailLabel` takes its hover color from this peer.
    "peer/rail-button relative m-cds-4 flex h-8 w-8 items-center justify-center rounded-cds-6",
    // An invisible hit area covers the button's 4px margin, which is all that separates icons, so
    // the rail has no dead space between them; with its label open, it also covers the label (4px
    // margin and a 12px line) and half the gap around a labeled icon. It reaches the rail's left
    // edge (16px), but only 4px to the right (RAIL_BUTTON_HIT_RIGHT_PX), which leaves the channel
    // to the hover panel, where a resting pointer dismisses the panel.
    labelShown
      ? "before:-left-[16px] before:-right-cds-4 before:-top-[10px] before:-bottom-[24px]"
      : "before:-left-[16px] before:-right-cds-4 before:-inset-y-cds-4",
    "before:absolute before:content-['']",
    "transition-colors duration-75",
    active && inertWhenActive ? "cursor-default" : "cursor-pointer",
    "focus-visible:outline-none focus-visible:!shadow-cds-focus",
    active
      ? [
          railActiveTextClassName(isDarkMode),
          !activeFillSlides && railActiveFillClassName(isDarkMode),
        ]
      : isDarkMode
        ? "text-zinc-400 hover:bg-white/[0.08] hover:text-zinc-50"
        : "text-cds-ink-subtle hover:!bg-zinc-200/60 hover:text-cds-ink-strong",
    highlighted &&
      !active &&
      (isDarkMode ? "bg-white/[0.08] text-zinc-50" : "!bg-zinc-200/60 text-cds-ink-strong"),
    admin && CHALK_ADMIN_SURFACE_CLASS,
  );

/**
 * The 10px label under a category's rail button, colored to match its icon. It shows only while
 * the sidebar is open, opening and closing over the sidebar's own 200ms width transition so the
 * icons below move with it. It must directly follow its button, whose hover it reads as a peer.
 * The ref is the clipped box, whose height runs between 0 and its content's.
 */
const RailLabel = React.forwardRef<
  HTMLDivElement,
  {
    label: string;
    shown: boolean;
    active: boolean;
    highlighted: boolean;
    isDarkMode: boolean;
  }
>(function RailLabel({ label, shown, active, highlighted, isDarkMode }, ref) {
  return (
    <div
      aria-hidden={true}
      className={classNames(
        "pointer-events-none grid w-full ease-out motion-reduce:transition-none",
        "[transition-duration:200ms,200ms,75ms] [transition-property:grid-template-rows,opacity,color]",
        shown ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        active || highlighted
          ? isDarkMode
            ? "text-white"
            : "text-cds-ink-strong"
          : isDarkMode
            ? "text-zinc-400 peer-hover/rail-button:text-zinc-50"
            : "text-cds-ink-subtle peer-hover/rail-button:text-cds-ink-strong",
      )}
    >
      {/* Padding lives on the text, not this box, so the box can close all the way to 0. The
          text sits flush under its icon, and the 14px below it is the gap between labeled icons;
          unlabeled, they sit flush. */}
      <div ref={ref} className={"min-h-0 overflow-hidden"}>
        {/* 6px in from each side of the 64px rail, so a long name's ellipsis keeps clear of the
            rail's edges; every category name still fits whole. */}
        <span
          className={
            "block truncate px-cds-6 pb-[14px] pt-0 text-center text-[10px] font-medium leading-3"
          }
        >
          {label}
        </span>
      </div>
    </div>
  );
});

/** The rail's current-page fill, at the top-left of the button it marks. */
const RailActiveFill: React.FC<{
  left: number;
  top: number;
  sliding: boolean;
  isDarkMode: boolean;
}> = ({ left, top, sliding, isDarkMode }) => {
  const ref = useRef<HTMLSpanElement | null>(null);
  const transform = `translate(${left}px, ${top}px)`;
  useTransitionAcrossPages("category-rail:active-fill", ref, "transform", transform, {
    animated: sliding,
  });

  return (
    <span
      ref={ref}
      aria-hidden={true}
      className={classNames(
        "pointer-events-none absolute left-0 top-0 h-8 w-8 rounded-cds-6",
        railActiveFillClassName(isDarkMode),
        sliding &&
          `transition-[transform] ${MOTION_DURATION_CLASS.medium} ease-out motion-reduce:transition-none`,
      )}
      style={{ transform }}
    />
  );
};

// Where the last rail's current-page fill sat, relative to the rail. Each page mounts its own rail,
// so a new one starts its fill here and slides it to its own category.
let previousRailActiveFillTop: number | undefined;

const RAIL_PIN_MENU_WIDTH_PX = 176;

/**
 * A sidebar item pinned to the rail: its icon, named by a tooltip, going straight to its page.
 * Right-clicking offers to unpin it.
 */
const RailPinButton: React.FC<{
  item: SideNavV2Item<string>;
  active: boolean;
  isDarkMode: boolean;
  redSurfacesSuppressed: boolean;
  /** A pin is being dragged, so the name tooltips stay out of the way. */
  dragging: boolean;
  /** This pin is the one being dragged, so it shows its hover fill. */
  lifted: boolean;
  onUnpin: () => void;
  /** Called as the pin starts navigating to its page. */
  onNavigate: () => void;
}> = ({
  item,
  active,
  isDarkMode,
  redSurfacesSuppressed,
  dragging,
  lifted,
  onUnpin,
  onNavigate,
}) => {
  const overlayPortalRoot = useOverlayPortalRoot();
  const router = useRouter();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ left: number; top: number } | null>(null);
  const menuOpen = menuPosition != null;
  const closeMenu = useCallback(() => setMenuPosition(null), []);

  useEffect(() => {
    if (!menuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      closeMenu();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeMenu, menuOpen]);

  return (
    <>
      {/* Pins are plain icons, so the name shows here: centred on the icon's highlight, 8px to
          its right. */}
      <TooltipV2
        content={item.label}
        placement={"right"}
        offset={8}
        persistOnHover={false}
        disabled={dragging}
      >
        <button
          type={"button"}
          aria-label={item.label}
          aria-current={active ? "page" : undefined}
          // Marks the menu as open so the tooltip steps aside for it.
          aria-expanded={menuOpen || undefined}
          className={railButtonClassName({
            active,
            highlighted: menuOpen || lifted,
            admin: item.admin === true && !redSurfacesSuppressed,
            isDarkMode,
            inertWhenActive: true,
          })}
          onClick={() => {
            // Like the current category's icon, the current page's pin does nothing.
            if (active || item.href == null) return;
            item.onSelect?.();
            onNavigate();
            void router.push(item.href);
          }}
          onContextMenu={(event) => {
            event.preventDefault();
            setMenuPosition({ left: event.clientX, top: event.clientY });
          }}
        >
          <Icon
            name={item.icon ?? "pin"}
            size={"md"}
            aria-hidden
            className={RAIL_ICON_SIZE_CLASS}
          />
        </button>
      </TooltipV2>
      {menuPosition != null &&
        typeof document !== "undefined" &&
        createPortal(
          <ViewportAwareFixedMenuV2
            ref={menuRef}
            className={"design-system-typography text-cds-md"}
            style={{ zIndex: ZIndex.TOOLTIP }}
            left={menuPosition.left}
            top={menuPosition.top}
            width={RAIL_PIN_MENU_WIDTH_PX}
            onMouseLeave={closeMenu}
          >
            <MenuContainerV2 className={"w-full"}>
              <MenuSectionV2>
                <MenuItemV2 size={"sm"} variant={"title"} label={item.label} />
                <MenuItemV2
                  size={"sm"}
                  label={"Unpin from rail"}
                  leftIcon={"pinOff"}
                  onClick={() => {
                    closeMenu();
                    onUnpin();
                  }}
                />
              </MenuSectionV2>
            </MenuContainerV2>
          </ViewportAwareFixedMenuV2>,
          overlayPortalRoot ?? document.body,
        )}
    </>
  );
};

const getRailRowId = (row: { id: string }) => row.id;

/**
 * The outline a rail icon shows while a sidebar row is dragged, if the icon's category would take
 * it: dashed, in the sidebar's drop stroke, as its drop slot is, and solid once the row is over it.
 */
const RailRowDropOutline: React.FC<{ over: boolean }> = ({ over }) => (
  <span
    aria-hidden={true}
    data-rail-row-drop-outline={over ? "over" : ""}
    className={classNames(
      "pointer-events-none absolute inset-0 rounded-cds-6 border-[1px]",
      "border-cds-primary-stroke-default",
      over ? "border-solid" : "border-dashed",
    )}
  />
);

/** The pinned shortcuts, which the reader orders by dragging (`useDragReorder`). */
const RailPinList: React.FC<{
  pins: readonly SideNavV2Item<string>[];
  activeItemId: string | undefined;
  isDarkMode: boolean;
  redSurfacesSuppressed: boolean;
  onUnpin: (itemId: string) => void;
  /** Stores the visible pins' new order. */
  onReorder: (itemIds: readonly string[]) => void;
  onNavigate: (itemId: string) => void;
}> = ({
  pins,
  activeItemId,
  isDarkMode,
  redSurfacesSuppressed,
  onUnpin,
  onReorder,
  onNavigate,
}) => {
  const reorder = useDragReorder({ items: pins, getId: getRailRowId, onReorder });

  return (
    <>
      {/* Scrolls on its own so a long list of pins never pushes the categories away. It opts out
          of the global stable scrollbar gutter, which would narrow the list and pull its centred
          icons left, and hides the scrollbar, which the rail has no room for. Positioned, so its
          pins' offsets are measured from it. */}
      <ul
        ref={(node) => {
          reorder.listRef.current = node;
        }}
        aria-label={"Pinned shortcuts"}
        className={classNames(
          "no-scrollbar-gutter relative flex min-h-0 w-full flex-1 flex-col items-center",
          "overflow-y-auto pb-cds-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        )}
      >
        {reorder.orderedItems.map((pin) => (
          <li
            key={pin.id}
            ref={reorder.registerRow(pin.id)}
            className={classNames(reorder.liftedId === pin.id && "relative z-10")}
            onPointerDown={(event) => reorder.onRowPointerDown(event, pin.id)}
            onClickCapture={reorder.onRowClickCapture}
          >
            <RailPinButton
              item={pin}
              active={pin.id === activeItemId}
              isDarkMode={isDarkMode}
              redSurfacesSuppressed={redSurfacesSuppressed}
              dragging={reorder.draggingId != null}
              lifted={reorder.liftedId === pin.id}
              onUnpin={() => onUnpin(pin.id)}
              onNavigate={() => onNavigate(pin.id)}
            />
          </li>
        ))}
      </ul>
      {reorder.overlay}
    </>
  );
};

/**
 * The leftmost rail, on the backing canvas: the workspace mark, whose category is Getting started
 * and the global settings pages, then one icon per navigation category.
 *
 * Hovering an icon floats that category's sidebar over the page. Clicking another category
 * navigates to its first destination; the current category's icon is inert.
 */
export const ProjectCategoryRailV2: React.FC<ProjectNavbarProps> = (props) => {
  const overlayPortalRoot = useOverlayPortalRoot();
  const router = useRouter();
  const { projectId, environmentId } = useRouteAndTeamInfo();
  const themeMode = useSideNavThemeMode();
  const redSurfacesSuppressed = useRedSurfacesSuppressed();
  const model = useProjectCategoryNavModel(props);
  const { categories, activeCategory } = model;
  const [pinned, setPinned] = useProjectSecondarySidebarPinned(
    props.defaultCollapsed === true || props.forceCollapsed === true,
    model.categorySettingsActive,
  );
  const { pinnedItemIds, unpinFromRail, setVisibleRailPinOrder } = useProjectRailPins();
  const { rowLayout } = useSidebarRowLayout();
  const { sectionOrders } = useSidebarSectionOrders();
  const rowDrop = useCategoryRailRowDrop();
  const pinnableRailItems = useMemo(() => collectPinnableRailItems(categories), [categories]);
  const railPins = useMemo(
    () => resolveRailPins(pinnedItemIds, pinnableRailItems),
    [pinnableRailItems, pinnedItemIds],
  );
  const railRef = useRef<HTMLElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [hoveredCategoryId, setHoveredCategoryId] = useState<string>();
  const [hoverPanelGeometry, setHoverPanelGeometry] = useState<HoverPanelGeometry>({
    contentLeft: 0,
    contentTop: FALLBACK_SIDEBAR_CONTENT_TOP_PX,
    contentHeight: 0,
  });
  const isDarkMode = themeMode === "dark";
  const themeClass =
    themeMode === "dark" ? "dark" : themeMode === "light" ? "theme-light" : undefined;

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current == null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);
  // Leaving the panel's icon starts Floating UI's safe polygon: the panel stays open while the
  // pointer travels the triangle from where it left the icon to the panel's edge, and other icons
  // it crosses on the way do not take over until it stops on one or turns away from the panel.
  const hoverPanelRef = useRef<HTMLDivElement | null>(null);
  const safePolygonTraversalRef = useRef<{ stop: () => void } | null>(null);
  const crossedRailIconIdRef = useRef<string | null>(null);
  const stopSafePolygon = useCallback(() => {
    safePolygonTraversalRef.current?.stop();
    safePolygonTraversalRef.current = null;
    crossedRailIconIdRef.current = null;
  }, []);
  // A row click navigates, but the page underneath changes only once the next page's server
  // props arrive, which can take a second. Closing on click would uncover the current page's own
  // sidebar for that gap, so the panel holds until the new page replaces it (a new page mounts its
  // own rail with the panel closed; a same-page route change closes it below).
  const holdingForNavigationRef = useRef(false);
  const routeChangePendingRef = useRef(false);
  const { state: appState, updateState: updateAppState } = useGlobalAppState();
  const navigationGraceTimerRef = useRef<number | null>(null);
  const releaseNavigationHold = useCallback(() => {
    holdingForNavigationRef.current = false;
    if (navigationGraceTimerRef.current == null) return;
    window.clearTimeout(navigationGraceTimerRef.current);
    navigationGraceTimerRef.current = null;
  }, []);
  const closeHoverPanel = useCallback(
    (reason: string) => {
      logHoverPanel(`close: ${reason}`);
      clearCloseTimer();
      stopSafePolygon();
      releaseNavigationHold();
      setHoveredCategoryId(undefined);
    },
    [clearCloseTimer, releaseNavigationHold, stopSafePolygon],
  );
  const canArrange = useCanArrangeCategoryNav();
  const { categoryOrder, setVisibleCategoryOrder } = useRailCategoryOrder();
  const orderedCategories = useMemo(
    () => applyStoredOrder(categories, categoryOrder),
    [categories, categoryOrder],
  );
  const categoryReorder = useDragReorder({
    items: orderedCategories,
    getId: getRailRowId,
    onReorder: setVisibleCategoryOrder,
    onDragStart: () => closeHoverPanel("category drag"),
  });
  // While icons move into a new order, the sliding fill stands down and the current icon paints
  // its own, which moves with it.
  const categoryRowsMoving = categoryReorder.liftedId != null;
  const holdHoverPanelForNavigation = useCallback(() => {
    logHoverPanel("hold: row selected");
    clearCloseTimer();
    releaseNavigationHold();
    holdingForNavigationRef.current = true;
    // A click that starts no route change (the page already open, a new-tab click) has nothing to
    // wait for.
    navigationGraceTimerRef.current = window.setTimeout(() => {
      navigationGraceTimerRef.current = null;
      if (!routeChangePendingRef.current) closeHoverPanel("row selected without navigation");
    }, HOVER_PANEL_NAVIGATION_START_GRACE_MS);
  }, [clearCloseTimer, closeHoverPanel, releaseNavigationHold]);
  // For the same gap, the current-page fill moves to a category as soon as it is chosen rather than
  // once its page mounts; that page's rail then starts the fill where this one left it. The fill
  // returns to the route's category if no route change starts or the change fails.
  const [chosenCategoryId, setChosenCategoryId] = useState<string>();
  const chosenCategoryTimerRef = useRef<number | null>(null);
  const clearChosenCategory = useCallback(() => {
    if (chosenCategoryTimerRef.current != null) {
      window.clearTimeout(chosenCategoryTimerRef.current);
      chosenCategoryTimerRef.current = null;
    }
    setChosenCategoryId(undefined);
  }, []);
  const { collapsed: railCollapsed, anticipate: anticipateRailCollapse } =
    useCategoryRailCollapse();
  const railCollapseTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (railCollapseTimerRef.current != null) window.clearTimeout(railCollapseTimerRef.current);
    },
    [],
  );
  const chooseCategory = useCallback(
    (categoryId: string | undefined) => {
      if (categoryId == null) return;
      // The global settings pages push the environment's icons off-screen. The floating sidebar
      // would ride along over the slide, so it fades out first and the slide follows.
      if (categoryId === GLOBAL_NAV_CATEGORY_ID) {
        closeHoverPanel("global settings chosen");
        if (railCollapseTimerRef.current != null) window.clearTimeout(railCollapseTimerRef.current);
        railCollapseTimerRef.current = window.setTimeout(() => {
          railCollapseTimerRef.current = null;
          anticipateRailCollapse(true);
        }, MOTION_DURATION_MS.short);
      }
      if (chosenCategoryTimerRef.current != null)
        window.clearTimeout(chosenCategoryTimerRef.current);
      setChosenCategoryId(categoryId);
      chosenCategoryTimerRef.current = window.setTimeout(() => {
        chosenCategoryTimerRef.current = null;
        if (!routeChangePendingRef.current) setChosenCategoryId(undefined);
      }, HOVER_PANEL_NAVIGATION_START_GRACE_MS);
    },
    [anticipateRailCollapse, closeHoverPanel],
  );
  useEffect(() => {
    const handleStart = () => {
      routeChangePendingRef.current = true;
    };
    const handleEnd = () => {
      routeChangePendingRef.current = false;
      clearChosenCategory();
    };
    const handleError = () => {
      routeChangePendingRef.current = false;
      clearChosenCategory();
      if (holdingForNavigationRef.current) closeHoverPanel("navigation failed");
      updateAppState({ [PIN_ON_ARRIVAL_STATE_KEY]: undefined });
    };
    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleEnd);
    router.events.on("routeChangeError", handleError);
    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleEnd);
      router.events.off("routeChangeError", handleError);
      releaseNavigationHold();
    };
  }, [clearChosenCategory, closeHoverPanel, releaseNavigationHold, router.events, updateAppState]);
  useEffect(
    () => () => {
      if (chosenCategoryTimerRef.current != null)
        window.clearTimeout(chosenCategoryTimerRef.current);
    },
    [],
  );
  // A row's "…" menu renders outside the panel, so the panel holds open while one is up and
  // closes once it shuts with the pointer elsewhere.
  const actionsMenuOpenRef = useRef(false);
  // A drag in the panel's sidebar carries the pointer off it, so it holds the panel open too.
  const reorderDraggingRef = useRef(false);
  const pointerInHoverPanelRef = useRef(false);
  const pointerOnRailIconRef = useRef(false);
  // In the channel, closing is left to the channel's rest timer.
  const pointerInChannelRef = useRef(false);
  // Blur only closes a panel that focus opened; a mouse-opened panel must survive the page
  // moving focus programmatically, as the SQL explorer does with its editor.
  const openedByFocusRef = useRef(false);
  const scheduleHoverPanelClose = useCallback(
    (reason: string) => {
      clearCloseTimer();
      if (
        actionsMenuOpenRef.current ||
        reorderDraggingRef.current ||
        holdingForNavigationRef.current
      ) {
        return;
      }
      closeTimerRef.current = window.setTimeout(() => {
        closeTimerRef.current = null;
        // The pointer can come to rest on an icon or the panel without a fresh enter event, such
        // as when content moves beneath it; it still counts as hovering.
        if (
          pointerOnRailIconRef.current ||
          pointerInHoverPanelRef.current ||
          pointerInChannelRef.current
        ) {
          logHoverPanel(
            `close skipped, pointer still over the rail, panel, or channel (${reason})`,
          );
          return;
        }
        logHoverPanel(`close: ${reason}`);
        setHoveredCategoryId(undefined);
      }, HOVER_PANEL_CLOSE_DELAY_MS);
    },
    [clearCloseTimer],
  );
  const handleActionsMenuOpenChange = useCallback(
    (open: boolean) => {
      actionsMenuOpenRef.current = open;
      if (!open && !pointerInHoverPanelRef.current) scheduleHoverPanelClose("actions menu closed");
    },
    [scheduleHoverPanelClose],
  );
  const handleReorderDragChange = useCallback(
    (dragging: boolean) => {
      reorderDraggingRef.current = dragging;
      if (dragging) return;
      // The panel's leave arrived as the drag's cover went up; read where the pointer ended up.
      const overPanel = hoverPanelRef.current?.matches(":hover") === true;
      pointerInHoverPanelRef.current = overPanel;
      if (!overPanel) scheduleHoverPanelClose("drag ended off the panel");
    },
    [scheduleHoverPanelClose],
  );
  const openHoverPanel = useCallback(
    (categoryId: string) => {
      clearCloseTimer();
      stopSafePolygon();
      setHoverPanelGeometry(measureHoverPanelGeometry(railRef.current));
      logHoverPanel(`open: ${categoryId}`);
      setHoveredCategoryId(categoryId);
    },
    [clearCloseTimer, stopSafePolygon],
  );

  useEffect(() => clearCloseTimer, [clearCloseTimer]);
  useEffect(() => stopSafePolygon, [stopSafePolygon]);
  // Only a move to another page closes the panel. Pages such as the SQL explorer rewrite their
  // query string as they sync state, and that must not dismiss a panel the pointer is still on.
  const routeKey = getProjectNavigationRouteKey(router.asPath);
  useEffect(() => {
    closeHoverPanel(`route ${routeKey}`);
  }, [closeHoverPanel, routeKey]);

  // A category pinned from another page's hover panel arrives here, and its sidebar pins once.
  const pinOnArrival = appState[PIN_ON_ARRIVAL_STATE_KEY];
  useEffect(() => {
    if (pinOnArrival == null || pinOnArrival !== activeCategory?.id) return;
    setPinned(true);
    updateAppState({ [PIN_ON_ARRIVAL_STATE_KEY]: undefined });
  }, [activeCategory?.id, pinOnArrival, setPinned, updateAppState]);
  // A request this page's rail mounted with names another category: its navigation landed
  // elsewhere, so it lapses rather than pinning a later visit. (The page that asked has mounted
  // already, and drops it only if its navigation fails.)
  const pinOnArrivalAtMountRef = useRef(pinOnArrival);
  const activeCategoryIdAtMount = activeCategory?.id;
  useEffect(() => {
    const requested = pinOnArrivalAtMountRef.current;
    if (requested == null || activeCategoryIdAtMount == null) return;
    if (requested !== activeCategoryIdAtMount) {
      updateAppState({ [PIN_ON_ARRIVAL_STATE_KEY]: undefined });
    }
    // Mount only: later requests come from this page's own hover panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // A global settings page's sidebar stays open, so there is nothing to pin there.
    if (activeCategory == null || model.onGlobalSettingsPage) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        !isSidebarToggleShortcut(event) ||
        isEditableKeyboardTarget(event.target)
      ) {
        return;
      }

      event.preventDefault();
      closeHoverPanel("sidebar shortcut");
      setPinned(!pinned);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [activeCategory, closeHoverPanel, model.onGlobalSettingsPage, pinned, setPinned]);

  // Every category a rail button opens: the workspace mark's global one, then the icons'.
  const railCategories = useMemo(
    () => [model.globalCategory, ...categories],
    [categories, model.globalCategory],
  );
  const hoveredCategory = railCategories.find((category) => category.id === hoveredCategoryId);
  // The current category's sidebar is already on screen while pinned (always, on a global settings
  // page), so hovering it adds nothing.
  const sidebarShown = pinned || model.onGlobalSettingsPage;
  const hoverPanelCategory =
    hoveredCategory != null && !(hoveredCategory.id === activeCategory?.id && sidebarShown)
      ? hoveredCategory
      : undefined;
  // Keyed by id: the nav model can hand back a fresh category object on any render, which must not
  // restart the enter transition.
  // A resize while the panel is open moves the column it floats inside.
  const hoverPanelOpen = hoverPanelCategory != null;
  useEffect(() => {
    if (!hoverPanelOpen) return;
    const remeasure = () => setHoverPanelGeometry(measureHoverPanelGeometry(railRef.current));
    window.addEventListener("resize", remeasure);
    return () => window.removeEventListener("resize", remeasure);
  }, [hoverPanelOpen]);
  const hoverPanelPresence = useTransitionPresence(
    hoverPanelCategory?.id,
    MOTION_DURATION_MS.short,
  );
  const renderedHoverPanelCategory = railCategories.find(
    (category) => category.id === hoverPanelPresence.rendered,
  );

  // Floating UI supplies only the context `safePolygon` reads. One panel serves every icon, so the
  // rail passes the icon being left as the reference itself rather than registering each icon with
  // `useHover`, which would bind an icon's leave listener only after the render that opened it;
  // the panel keeps its own fixed positioning, so the computed floating styles are unused.
  const { context: hoverPanelFloatingContext } = useFloating({
    open: hoverPanelCategory != null,
    placement: "right-start",
  });
  const startSafePolygon = (reference: HTMLElement, panel: HTMLElement, leaveEvent: MouseEvent) => {
    stopSafePolygon();
    clearCloseTimer();
    const traversal = {
      stop: () => document.removeEventListener("mousemove", handleMouseMove),
    };
    const handleMouseMove = safePolygon()({
      ...hoverPanelFloatingContext,
      elements: { reference, domReference: reference, floating: panel },
      x: leaveEvent.clientX,
      y: leaveEvent.clientY,
      // Off the path to the panel, or resting on it: an icon crossed on the way takes over, and
      // otherwise the panel closes as it would on leaving the icon.
      onClose: () => {
        // A timer from the last move inside the polygon can still fire after the traversal ends.
        if (safePolygonTraversalRef.current !== traversal) return;
        const crossedCategoryId = crossedRailIconIdRef.current;
        stopSafePolygon();
        if (crossedCategoryId != null) {
          openHoverPanel(crossedCategoryId);
          return;
        }
        scheduleHoverPanelClose("left the path to the panel");
      },
    });
    safePolygonTraversalRef.current = traversal;
    document.addEventListener("mousemove", handleMouseMove);
    logHoverPanel("safe polygon: started");
    // Judged once on the leave itself, as Floating UI's `useHover` does: a pointer that leaves
    // backwards closes at once, and one that leaves the window, where no move follows, still
    // closes once the intent timer runs out. React reports leaves from `mouseout`, which Floating
    // UI would read as a move over the icon, so the judgement gets a real `mouseleave`.
    handleMouseMove(
      new MouseEvent("mouseleave", {
        clientX: leaveEvent.clientX,
        clientY: leaveEvent.clientY,
        relatedTarget: leaveEvent.relatedTarget,
      }),
    );
  };

  // A global settings page's nav points into the environment the reader was last in, and its
  // Back to app link returns to the page they were on there.
  useEffect(() => {
    if (projectId == null || environmentId == null) return;
    setNavigationV2AppReturnPath(router.asPath);
  }, [environmentId, projectId, router.asPath]);

  // The current-page fill slides between the workspace mark and the category icons, as
  // SliderTabsV2's active pill slides between tabs.
  const railCategoryButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const railLabelRefs = useRef(new Map<string, HTMLDivElement>());
  const [activeFill, setActiveFill] = useState<{ left: number; top: number; sliding: boolean }>();
  // Leaving the global settings pages, the rail shows where Back to app leads as its icons return.
  const returnHref = useGlobalNavReturnHref();
  const returnCategoryId =
    model.onGlobalSettingsPage && !railCollapsed
      ? findProjectNavCategoryForPath(categories, returnHref)?.id
      : undefined;
  // The category the rail shows as current: one just chosen, until its page arrives.
  const railActiveCategoryId = chosenCategoryId ?? returnCategoryId ?? activeCategory?.id;
  // The icons' labels show only beside the open sidebar.
  const railLabelsShown = pinned && activeCategory != null;
  const railLabelsShownRef = useRef(railLabelsShown);
  useLayoutEffect(() => {
    const labelsToggled = railLabelsShownRef.current !== railLabelsShown;
    railLabelsShownRef.current = railLabelsShown;
    const rail = railRef.current;
    const button =
      railActiveCategoryId == null
        ? undefined
        : railCategoryButtonRefs.current.get(railActiveCategoryId);
    // Cleared, a fill that returns is placed rather than slid from where it was.
    if (rail == null || button == null || categoryRowsMoving) {
      previousRailActiveFillTop = undefined;
      setActiveFill(undefined);
      return;
    }

    const railRect = rail.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    // Every button is centred in a 64px column at the rail's left edge. Measured from the column
    // rather than the button, the fill ignores the icons' slide on and off the screen.
    const left = (RAIL_WIDTH_PX - buttonRect.width) / 2;
    // Labels above the button may be mid-way through opening or closing, so the fill aims for where
    // the button settles: each label's settled height (its content's, or 0) less its current one.
    const top =
      buttonRect.top -
      railRect.top +
      [...railLabelRefs.current.values()]
        .map((label) => ({ label, rect: label.getBoundingClientRect() }))
        .filter(({ rect }) => rect.bottom <= buttonRect.top)
        .reduce(
          (shift, { label, rect }) =>
            shift + (railLabelsShown ? label.scrollHeight : 0) - rect.height,
          0,
        );
    // The labels move the button over the fill's own 200ms ease-out, so the fill moves alongside.
    if (labelsToggled) {
      previousRailActiveFillTop = top;
      setActiveFill({ left, top, sliding: true });
      return;
    }

    const from = previousRailActiveFillTop;
    if (from == null || from === top) {
      previousRailActiveFillTop = top;
      setActiveFill({ left, top, sliding: false });
      return;
    }

    // Paints once where the fill was, then moves it, so the move has a start to transition from.
    // The new position is recorded only once the move starts, so a cancelled run starts over.
    setActiveFill({ left, top: from, sliding: false });
    let innerFrame = 0;
    const outerFrame = window.requestAnimationFrame(() => {
      innerFrame = window.requestAnimationFrame(() => {
        previousRailActiveFillTop = top;
        setActiveFill({ left, top, sliding: true });
      });
    });
    return () => {
      window.cancelAnimationFrame(outerFrame);
      window.cancelAnimationFrame(innerFrame);
    };
  }, [categoryRowsMoving, railActiveCategoryId, railLabelsShown]);

  // Each page mounts its own rail, so moves still under way when the next page arrives continue there.
  const railBodyRef = useRef<HTMLDivElement | null>(null);
  const railWidth = railCollapsed ? "0px" : `${RAIL_WIDTH_PX}px`;
  const railBodyTransform = railCollapsed ? "translateX(-100%)" : "translateX(0%)";
  useTransitionAcrossPages("category-rail:width", railRef, "width", railWidth);
  useTransitionAcrossPages("category-rail:body", railBodyRef, "transform", railBodyTransform);

  const selectCategory = (category: SideNavV2Section<string>) => {
    // The sidebar toggle and its shortcut pin or unpin the sidebar; the current icon does nothing.
    if (category.id === activeCategory?.id) return;

    const landingItem = getCategoryLandingItem(category, categories, rowLayout, sectionOrders);
    if (landingItem?.href == null) return;

    landingItem.onSelect?.();
    chooseCategory(category.id);
    void router.push(landingItem.href);
  };
  // Pins the hover panel's sidebar. The pinned sidebar shows the page's own category, so another
  // category's opens its first page, as its icon does, and that page's rail pins the sidebar on
  // arrival (`PIN_ON_ARRIVAL_STATE_KEY`): pinning here would open this page's sidebar under the
  // panel for the length of the navigation.
  const pinHoverPanelCategory = (category: SideNavV2Section<string>) => {
    if (category.id === activeCategory?.id) {
      setPinned(true);
      return;
    }
    const landingItem = getCategoryLandingItem(category, categories, rowLayout, sectionOrders);
    if (landingItem?.href == null) return;

    updateAppState({ [PIN_ON_ARRIVAL_STATE_KEY]: category.id });
    landingItem.onSelect?.();
    holdHoverPanelForNavigation();
    chooseCategory(category.id);
    void router.push(landingItem.href);
  };

  // Shared by the workspace mark and the category icons: hovering or focusing one floats its
  // category's sidebar, and clicking it selects the category.
  const railCategoryButtonProps = (category: SideNavV2Section<string>) => ({
    type: "button" as const,
    ref: (node: HTMLButtonElement | null) => {
      if (node == null) railCategoryButtonRefs.current.delete(category.id);
      else railCategoryButtonRefs.current.set(category.id, node);
    },
    "aria-current": category.id === activeCategory?.id ? ("page" as const) : undefined,
    "aria-expanded": category.id === hoverPanelCategory?.id,
    onMouseEnter: () => {
      pointerOnRailIconRef.current = true;
      openedByFocusRef.current = false;
      // Crossed on the way from another icon to its panel: the safe polygon decides whether this
      // icon takes over.
      if (safePolygonTraversalRef.current != null && category.id !== hoverPanelCategory?.id) {
        crossedRailIconIdRef.current = category.id;
        return;
      }
      openHoverPanel(category.id);
    },
    onMouseLeave: (event: React.MouseEvent<HTMLButtonElement>) => {
      pointerOnRailIconRef.current = false;
      if (crossedRailIconIdRef.current === category.id) {
        crossedRailIconIdRef.current = null;
        return;
      }
      if (category.id === hoverPanelCategory?.id && hoverPanelRef.current != null) {
        startSafePolygon(event.currentTarget, hoverPanelRef.current, event.nativeEvent);
        return;
      }
      scheduleHoverPanelClose(`left ${category.id} icon`);
    },
    onFocus: () => {
      if (pointerOnRailIconRef.current) return;
      openedByFocusRef.current = true;
      openHoverPanel(category.id);
    },
    onBlur: () => {
      if (openedByFocusRef.current) scheduleHoverPanelClose(`${category.id} icon blur`);
    },
    onClick: () => selectCategory(category),
  });

  // While a hover panel is open, a pointer resting in the channel between the rail's buttons and
  // the panel for HOVER_PANEL_CHANNEL_REST_MS closes it. The panel's bridge across the inset gap
  // is part of the channel, though it counts as the panel for its leave.
  const hoverPanelCategoryId = hoverPanelCategory?.id;
  useEffect(() => {
    if (hoverPanelCategoryId == null) return;
    let restTimer: number | null = null;
    const clearRestTimer = () => {
      if (restTimer != null) window.clearTimeout(restTimer);
      restTimer = null;
    };
    const handleMouseMove = (event: MouseEvent) => {
      const panel = hoverPanelRef.current;
      const button = railCategoryButtonRefs.current.get(hoverPanelCategoryId);
      if (panel == null || button == null) return;
      // Every button is centred in the rail at one width, so the panel's own marks where the
      // channel starts.
      const panelRect = panel.getBoundingClientRect();
      const inChannel =
        event.clientX >= button.getBoundingClientRect().right + RAIL_BUTTON_HIT_RIGHT_PX &&
        event.clientX < panelRect.left &&
        event.clientY >= panelRect.top &&
        event.clientY < panelRect.bottom;
      if (inChannel === pointerInChannelRef.current) return;
      pointerInChannelRef.current = inChannel;
      if (!inChannel) {
        clearRestTimer();
        // Onto the panel or an icon, it stays open; out onto the page, it closes as on any leave.
        scheduleHoverPanelClose("left the channel");
        return;
      }
      restTimer = window.setTimeout(() => {
        restTimer = null;
        if (
          actionsMenuOpenRef.current ||
          reorderDraggingRef.current ||
          holdingForNavigationRef.current
        ) {
          return;
        }
        // A panel fading out from under a pointer on its bridge may never report the leave.
        pointerInHoverPanelRef.current = false;
        closeHoverPanel("rested in the channel");
      }, HOVER_PANEL_CHANNEL_REST_MS);
    };
    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      clearRestTimer();
      pointerInChannelRef.current = false;
    };
  }, [closeHoverPanel, hoverPanelCategoryId, scheduleHoverPanelClose]);

  // A row dragged in the docked sidebar can move to another category by dropping on its icon:
  // anywhere across the rail in that icon's slot, label included. Settings takes no rows.
  const setRowDropCategoryAt = rowDrop?.setCategoryAt;
  useEffect(() => {
    if (setRowDropCategoryAt == null) return;
    setRowDropCategoryAt((clientX, clientY, fromCategoryId) => {
      const rail = railRef.current?.getBoundingClientRect();
      if (railCollapsed || rail == null || clientX < rail.left || clientX >= rail.right) {
        return null;
      }
      const target = orderedCategories.find((category) => {
        if (category.id === fromCategoryId || !categoryTradesRows(category)) return false;
        const slot = railCategoryButtonRefs.current
          .get(category.id)
          ?.closest("li")
          ?.getBoundingClientRect();
        return slot != null && clientY >= slot.top && clientY < slot.bottom;
      });
      return target?.id ?? null;
    });
    return () => setRowDropCategoryAt(null);
  }, [orderedCategories, railCollapsed, setRowDropCategoryAt]);

  return (
    <>
      <nav
        ref={railRef}
        aria-label={"Project navigation categories"}
        data-ds-v2={""}
        className={classNames(
          themeClass,
          // Collapsing narrows the rail to nothing while the workspace mark's 64px row overflows
          // it, so the mark stays put and the page takes the column (CategoryRailTopRailInset).
          "sticky top-0 flex h-screen flex-col items-start transition-[width]",
          CATEGORY_RAIL_COLLAPSE_TRANSITION_CLASS,
          "bg-cds-surface-active dark:bg-cds-inset-dark",
          isDarkMode ? "text-zinc-400" : "text-cds-ink-subtle",
        )}
        style={{ width: railWidth }}
      >
        {/* Painted before the buttons, so it sits beneath them. */}
        {activeFill != null && <RailActiveFill {...activeFill} isDarkMode={isDarkMode} />}
        {/* Level with the top rail, so the workspace mark lines up with its contents. */}
        <div className={"flex h-top-rail w-16 shrink-0 items-center justify-center"}>
          <button
            aria-label={model.globalCategory.label}
            className={railButtonClassName({
              active: model.globalCategory.id === railActiveCategoryId,
              highlighted: model.globalCategory.id === hoverPanelCategory?.id,
              admin: false,
              isDarkMode,
              activeFillSlides: true,
              inertWhenActive: true,
            })}
            {...railCategoryButtonProps(model.globalCategory)}
          >
            <SideNavWorkspaceAvatarV2
              label={model.workspaceLabel}
              themeMode={themeMode}
              decorative
              className={"!h-3 !w-3"}
            />
          </button>
        </div>
        {/* The environment's icons and pins, and the admin markers, which slide off-screen while
            collapsed. */}
        <div
          ref={railBodyRef}
          className={classNames(
            "flex min-h-0 w-16 flex-1 flex-col items-center transition-transform",
            CATEGORY_RAIL_COLLAPSE_TRANSITION_CLASS,
          )}
          style={{ transform: railBodyTransform }}
          inert={railCollapsed || undefined}
          aria-hidden={railCollapsed || undefined}
        >
          {/* The reader orders the icons by dragging them. Positioned, so their offsets are
              measured from it. */}
          <ul
            ref={(node) => {
              categoryReorder.listRef.current = node;
            }}
            className={"relative flex w-full flex-col"}
          >
            {categoryReorder.orderedItems.map((category) => {
              const admin = category.items.every((item) => item.admin === true);
              const active = category.id === railActiveCategoryId;
              const lifted = category.id === categoryReorder.liftedId;
              const rowDropTarget = category.id === rowDrop?.targetCategoryId;
              // While a sidebar row is dragged, the icons that take it show their outline.
              const takesDraggedRow =
                rowDrop?.draggingFromCategoryId != null &&
                category.id !== rowDrop.draggingFromCategoryId &&
                categoryTradesRows(category);
              const highlighted = category.id === hoverPanelCategory?.id || lifted || rowDropTarget;

              return (
                <li
                  key={category.id}
                  ref={categoryReorder.registerRow(category.id)}
                  className={classNames("flex flex-col items-center", lifted && "relative z-10")}
                  onPointerDown={
                    canArrange
                      ? (event) => categoryReorder.onRowPointerDown(event, category.id)
                      : undefined
                  }
                  onClickCapture={categoryReorder.onRowClickCapture}
                >
                  <button
                    aria-label={category.label ?? category.id}
                    className={railButtonClassName({
                      active,
                      highlighted,
                      admin: admin && !redSurfacesSuppressed,
                      isDarkMode,
                      activeFillSlides: !categoryRowsMoving,
                      labelShown: railLabelsShown,
                      inertWhenActive: true,
                    })}
                    {...railCategoryButtonProps(category)}
                  >
                    <Icon
                      name={getProjectNavCategoryIcon(category.id)}
                      size={"md"}
                      aria-hidden
                      className={RAIL_ICON_SIZE_CLASS}
                    />
                    {takesDraggedRow && <RailRowDropOutline over={rowDropTarget} />}
                  </button>
                  {/* The button carries the accessible name; this repeats it for sighted readers. */}
                  <RailLabel
                    ref={(node) => {
                      if (node == null) railLabelRefs.current.delete(category.id);
                      else railLabelRefs.current.set(category.id, node);
                    }}
                    label={category.label ?? category.id}
                    shown={railLabelsShown}
                    active={active}
                    highlighted={highlighted}
                    isDarkMode={isDarkMode}
                  />
                </li>
              );
            })}
          </ul>
          {categoryReorder.overlay}
          {railPins.length > 0 && (
            <>
              <span
                aria-hidden={true}
                className={
                  "my-cds-8 h-px w-cds-16 shrink-0 bg-cds-surface-border dark:bg-white/[0.08]"
                }
              />
              <RailPinList
                pins={railPins}
                activeItemId={model.activeItemId}
                isDarkMode={isDarkMode}
                redSurfacesSuppressed={redSurfacesSuppressed}
                onUnpin={unpinFromRail}
                onReorder={setVisibleRailPinOrder}
                onNavigate={(itemId) =>
                  chooseCategory(resolveRowCategory(categories, rowLayout, itemId)?.id)
                }
              />
            </>
          )}
          <div className={"mt-auto flex w-full shrink-0 flex-col items-center gap-cds-4 py-cds-8"}>
            {/* Admin mode and impersonation markers, as the collapsed sidebar shows them. */}
            <CollapsedNavbarAdminIndicators />
          </div>
        </div>
      </nav>
      {renderedHoverPanelCategory != null &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={classNames(
              themeClass,
              // Portaled outside LayoutV2, so it restates the shell's type scale and font, which
              // the pinned sidebar inherits from its ancestors.
              "design-system-typography text-cds-md",
              "fixed rounded-cds-6 border-[1px]",
              // Half translucent over a blur, so the page beneath shows only as soft shapes.
              // The panel's sidebar leaves its own surface clear, so this is the only fill.
              "border-cds-surface-border bg-cds-surface-default/50 shadow-cds-l backdrop-blur-md",
              "dark:border-white/[0.08] dark:bg-cds-elevated-dark/50 dark:shadow-cds-l-dark",
              `transition-[opacity,transform] ease-out ${MOTION_DURATION_CLASS.short}`,
              "motion-reduce:transition-none",
              hoverPanelPresence.shown
                ? "translate-x-0 opacity-100"
                : "pointer-events-none -translate-x-cds-4 opacity-0",
              ZIndexClass.NAVIGATION_FLYOUT,
            )}
            // Only the boundary insets: every edge moves in from the column the pinned sidebar
            // occupies (the elevated pane's edges, which sit below the top rail and above the
            // assistant's rail, and the pinned sidebar's right border), while the content below
            // stays where it sits when pinned.
            style={{
              left: hoverPanelGeometry.contentLeft - ELEVATED_PANE_BORDER_PX + HOVER_PANEL_INSET_PX,
              top: hoverPanelGeometry.contentTop - ELEVATED_PANE_BORDER_PX + HOVER_PANEL_INSET_PX,
              height:
                hoverPanelGeometry.contentHeight +
                2 * ELEVATED_PANE_BORDER_PX -
                2 * HOVER_PANEL_INSET_PX,
              width:
                PROJECT_SECONDARY_SIDEBAR_WIDTH_PX +
                ELEVATED_PANE_BORDER_PX -
                2 * HOVER_PANEL_INSET_PX,
            }}
            ref={hoverPanelRef}
            onMouseEnter={() => {
              pointerInHoverPanelRef.current = true;
              clearCloseTimer();
              stopSafePolygon();
            }}
            onMouseLeave={() => {
              pointerInHoverPanelRef.current = false;
              scheduleHoverPanelClose("left panel");
            }}
          >
            {/* Keeps the pointer inside the panel while it crosses the inset gap from the rail;
                resting on it still dismisses the panel, as anywhere in the channel. */}
            <div
              aria-hidden={true}
              className={"absolute right-full top-0 h-full"}
              style={{ width: HOVER_PANEL_INSET_PX + ELEVATED_PANE_BORDER_PX }}
            />
            <div className={"absolute inset-0 overflow-hidden rounded-[5px]"}>
              {/* Offset back by the inset so every row starts where it does when pinned. One
                  border narrower, so rows end as far inside the right edge as they start inside
                  the left. */}
              <div
                className={"absolute"}
                style={{
                  left: -HOVER_PANEL_INSET_PX,
                  top: -HOVER_PANEL_INSET_PX,
                  width: PROJECT_SECONDARY_SIDEBAR_WIDTH_PX - ELEVATED_PANE_BORDER_PX,
                  height: hoverPanelGeometry.contentHeight,
                }}
              >
                <ProjectCategorySidebarV2
                  key={renderedHoverPanelCategory.id}
                  category={renderedHoverPanelCategory}
                  model={model}
                  floating
                  // Only while the sidebar is hidden: pinned, the page's own sidebar carries the
                  // toggle. The global settings sidebar pins nothing: it shows on its own pages only.
                  sidebarToggle={
                    sidebarShown ||
                    renderedHoverPanelCategory.id === GLOBAL_NAV_CATEGORY_ID ? undefined : (
                      <HoverPanelPinButton
                        showsShortcut={renderedHoverPanelCategory.id === activeCategory?.id}
                        onPin={() => pinHoverPanelCategory(renderedHoverPanelCategory)}
                      />
                    )
                  }
                  onItemSelect={(item) => {
                    // A row opening in a new tab leaves this page, and its category, in place.
                    if (item.shouldOpenInNewTab === true) {
                      holdHoverPanelForNavigation();
                      return;
                    }
                    chooseCategory(renderedHoverPanelCategory.id);
                    // A global settings row dismisses the panel itself, ahead of the rail's slide.
                    if (renderedHoverPanelCategory.id !== GLOBAL_NAV_CATEGORY_ID) {
                      holdHoverPanelForNavigation();
                    }
                  }}
                  onItemActionsMenuOpenChange={handleActionsMenuOpenChange}
                  onReorderDragChange={handleReorderDragChange}
                />
              </div>
            </div>
          </div>,
          overlayPortalRoot ?? document.body,
        )}
    </>
  );
};
